"use client";

import { ConnectKitButton } from "connectkit";
import { useRouter, useSearchParams } from "next/navigation";
import Script from "next/script";
import { useEffect, useState } from "react";
import { useAccount } from "wagmi";
import PaymentForm from "./PaymentForm";
import PricingCard from "./PricingCard";
const RECAPTCHA_SITE_KEY = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || "";

export type ProductDeal = "half-price" | "full-price";

const BlockchainPayment = () => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const productDeal = searchParams.get("deal") as ProductDeal;
  const [showAfterConnection, setShowAfterConnection] = useState<ProductDeal | null>(null);
  const { isConnected } = useAccount();

  const buyAgent = (deal: ProductDeal, isConnected: boolean, show?: () => void) => {
    if (isConnected) {
      router.push(`?deal=${deal}`);
    } else {
      setShowAfterConnection(deal);
      show?.();
    }
  };

  useEffect(() => {
    if (showAfterConnection && isConnected) {
      router.push(`?deal=${showAfterConnection}`);
    }
  }, [showAfterConnection, router, isConnected]);

  return (
    <>
      <Script src={`https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`} async defer />
      {!productDeal ? (
        <>
          <div className="text-center mb-16">
            <h1 className="text-3xl lg:text-4xl font-bold mb-4">KOLZ AI Agents: Revolutionize Community Engagement</h1>
            <h2 className="text-2xl lg:text-3xl mb-8">AI-Powered Moderators for Discord, Telegram & Beyond</h2>
            <div className="flex flex-col gap-2 text-lg max-w-2xl mx-auto">
              <div className="flex items-center justify-center gap-1">
                <div className="w-2 h-2 rounded-full bg-turquoise mr-1" />
                <p className="m0">24/7 AI moderators tailored to your project</p>
              </div>
              <div className="flex items-center justify-center gap-1">
                <div className="w-2 h-2 rounded-full bg-turquoise mr-1" />
                <p className="m0">Trained on your FAQs, whitepapers & token details</p>
              </div>
              <div className="flex items-center justify-center gap-1">
                <div className="w-2 h-2 rounded-full bg-turquoise mr-1" />
                <p className="m0">Automate engagement and support</p>
              </div>
              <div className="flex items-center justify-center gap-1">
                <div className="w-2 h-2 rounded-full bg-turquoise mr-1" />
                <p className="m0">Seamless Discord & Telegram integration</p>
              </div>
            </div>
          </div>

          <h3 className="text-1xl lg:text-3xl font-semibold pb-3 md:pb-9">Transparent, Affordable Pricing</h3>
          <div className="flex flex-col md:flex-row items-center gap-6 opacity-0 translate-x-[-100%] animate-slide-in">
            <ConnectKitButton.Custom>
              {({ isConnected, show }) => (
                <PricingCard deal="full-price" isConnected={isConnected} show={show} buyAgent={buyAgent} />
              )}
            </ConnectKitButton.Custom>

            <ConnectKitButton.Custom>
              {({ isConnected, show }) => (
                <PricingCard deal="half-price" isConnected={isConnected} show={show} buyAgent={buyAgent} />
              )}
            </ConnectKitButton.Custom>
          </div>
          <div className="mt-12">
            <h3 className="text-3xl font-semibold mb-6">How It Works</h3>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="min-w-[24px] h-6 flex items-center justify-center rounded-full bg-turquoise text-white text-sm">
                  1
                </div>
                <p className="text-lg">Purchase Your AI Agent – Pay with ETH or $KOLZ</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="min-w-[24px] h-6 flex items-center justify-center rounded-full bg-turquoise text-white text-sm">
                  2
                </div>
                <p className="text-lg">Confirmation Email – Instantly receive payment verification</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="min-w-[24px] h-6 flex items-center justify-center rounded-full bg-turquoise text-white text-sm">
                  3
                </div>
                <p className="text-lg">
                  Agent Training Begins – A KOLZ team member will reach out within 48 hours to start training your AI
                  Agent
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="min-w-[24px] h-6 flex items-center justify-center rounded-full bg-turquoise text-white text-sm">
                  4
                </div>
                <p className="text-lg">Customization – AI is trained on your project&apos;s FAQs & content</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="min-w-[24px] h-6 flex items-center justify-center rounded-full bg-turquoise text-white text-sm">
                  5
                </div>
                <p className="text-lg">Launch the Bot – Deploy in your Discord and Telegram community</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="min-w-[24px] h-6 flex items-center justify-center rounded-full bg-turquoise text-white text-sm">
                  6
                </div>
                <p className="text-lg">
                  24/7 Engagement & Automation – AI moderates, educates, and supports your users
                </p>
              </div>
            </div>
          </div>
        </>
      ) : (
        <PaymentForm productDeal={productDeal} />
      )}
    </>
  );
};

export default BlockchainPayment;
